import {useEffect, useRef, useCallback} from 'react'

/**
 * Custom hook to preserve scroll position when modals open and close
 * This prevents the page from jumping to the top when a modal is closed
 */
export const useModalScrollPreservation = () => {
  const scrollPosition = useRef<number>(0)
  const isModalOpen = useRef<boolean>(false)

  const preserveScroll = useCallback(() => {
    if (!isModalOpen.current) {
      scrollPosition.current = window.pageYOffset || document.documentElement.scrollTop
      isModalOpen.current = true
    }
  }, [])

  const restoreScroll = useCallback(() => {
    if (isModalOpen.current) {
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition.current)
        isModalOpen.current = false
      })
    }
  }, [])

  const createCloseHandler = useCallback((originalOnClose?: () => void) => {
    return () => {
      // Use setTimeout to ensure the modal closes first, then restore scroll
      setTimeout(() => {
        restoreScroll()
      }, 0)
      
      if (originalOnClose) {
        originalOnClose()
      }
    }
  }, [restoreScroll])

  // Auto-preserve scroll when hook is used
  useEffect(() => {
    preserveScroll()
    
    // Cleanup function to restore scroll if component unmounts
    return () => {
      if (isModalOpen.current) {
        restoreScroll()
      }
    }
  }, [preserveScroll, restoreScroll])

  return {
    preserveScroll,
    restoreScroll,
    createCloseHandler,
    isModalOpen: isModalOpen.current
  }
}

export default useModalScrollPreservation